import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ApiSuccessResponse } from "@erp/shared";
import { apiClient } from "./client";

export interface PermissionRecord {
  id: string;
  key: string;
  resource: string;
  action: string;
}

export interface RoleRecord {
  id: string;
  code: string;
  name: string;
  description: string | null;
  isSystem: boolean;
  permissions: PermissionRecord[];
}

export function useRoles() {
  return useQuery({
    queryKey: ["roles"],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiSuccessResponse<RoleRecord[]>>("/roles");
      return data.data;
    },
  });
}

export function usePermissionsCatalog() {
  return useQuery({
    queryKey: ["roles", "permissions-catalog"],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiSuccessResponse<PermissionRecord[]>>("/roles/permissions-catalog");
      return data.data;
    },
  });
}

export interface CreateRolePayload {
  code: string;
  name: string;
  description?: string;
  permissionIds: string[];
}

export function useCreateRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateRolePayload) => {
      const { data } = await apiClient.post<ApiSuccessResponse<RoleRecord>>("/roles", payload);
      return data.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["roles"] }),
  });
}

export function useUpdateRolePermissions() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, permissionIds }: { id: string; permissionIds: string[] }) => {
      const { data } = await apiClient.patch<ApiSuccessResponse<RoleRecord>>(`/roles/${id}/permissions`, { permissionIds });
      return data.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["roles"] }),
  });
}

export function useDeleteRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/roles/${id}`);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["roles"] }),
  });
}
